<template>
  <li class="device">
    <div class="device-data">
      <div class="device-name">
        {{ device.name }}
      </div>

      <div v-if="seenWithinLast5Min" class="standard-mode">
        {{ $t("devices.seenWithinLast5Minutes") }}
      </div>

      <div v-else-if="deviceMode" class="last-used">
        {{ $t("devices.lastSeen", { lastUsedTime }) }}
      </div>

      <div v-if="notActiveLast8Hrs" class="limited-mode">
        {{ $t("alerts.deviceOutOfContact") }}
      </div>

      <div v-else-if="notActiveLast24Hrs" class="limited-mode">
        {{ $t("alerts.NetNannyMightBeRemoved") }}
      </div>

      <div v-else-if="deviceUnrestricted" class="limited-mode">
        {{ $t("alerts.deviceSetToNoRestrictions") }}
      </div>

      <div v-else-if="deviceMode" class="device-rule">
        {{ $t("devices.activeRule") }}

        <span :class="modeStyles">
          {{ deviceMode.name }}
        </span>
      </div>
    </div>

    <div v-if="device.id === primaryDeviceId" class="primary-label">
      {{ $t("primary") | upperCase }}
    </div>

    <base-button v-else
                 class="set-device-btn"
                 color="secondary"
                 :loading="loading"
                 @click="setAsPrimary">
      {{ $t("devices.setAsPrimary") }}
    </base-button>
  </li>
</template>

<script>
  import analytics from '@/services/analytics.js'
  import errorNotification from '@/mixins/errorNotification.js'
  import { dateDiffFromNow } from '@/services/intl'

  export default {
    mixins: [
      errorNotification
    ],

    props: {
      childId: {
        type: String,
        required: true
      },

      deviceId: {
        type: String,
        required: true
      }
    },

    data() {
      return {
        loading: false
      }
    },

    computed: {
      ...mapGetters('children', {
        getChildById: 'getById'
      }),

      child() {
        return this.getChildById(this.childId)
      },

      device() {
        return this.child.devices.find(device => device.id === this.deviceId)
      },

      seenWithinLast5Min() {
        const minutes_difference = dateDiffFromNow(this.device.last_seen, 'minute')

        return minutes_difference <= 5
      },

      notActiveLast8Hrs() {
        const hours_difference = dateDiffFromNow(this.device.last_seen, 'hour')

        return hours_difference >= 8 && hours_difference < 24
      },

      notActiveLast24Hrs() {
        const hours_difference = dateDiffFromNow(this.device.last_seen, 'hour')

        return hours_difference >= 24
      },

      modeStyles() {
        return {
          'standard-mode': this.deviceMode.name === 'Standard',
          'limited-mode': this.deviceMode.name !== 'Standard'
        }
      },

      modes() {
        return this.child.modes
      },

      deviceMode() {
        return this.modes.find(mode => mode.id === this.device.current_mode_id)
      },

      primaryDeviceId() {
        return this.child.primary_device_id
      },

      lastUsedTime() {
        return this.$options.filters.timeAgo(this.device.last_seen)
      },

      deviceUnrestricted() {
        return this.device.reason === 'unrestricted'
      }
    },

    methods: {
      setAsPrimary() {
        analytics.sendEvent('Pk_PrimDvc_EditCh')

        this.loading = true

        this.changePrimaryDevice({
          childId: this.child.id,
          deviceId: this.device.id
        }).then(() => {
          this.loading = false
        })
        .catch(() => {
          this.failedRequestNotification()
        })
      },

      ...mapActions('children', {
        changePrimaryDevice: 'changePrimaryDevice'
      })
    }
  }
</script>

<style lang="scss" scoped>
  .device {
    display: flex;
    justify-content: space-between;
    align-items: center;

    border-bottom: 1px solid #f2f2f2;
    padding: 12px 0;

    &-data {
      .device-name {
        font-size: 24px;
        font-weight: bold;
        color: map-get($colors , primary);
      }

      .standard-mode {
        color: #00aa19;
      }

      .limited-mode {
        color: #fb5e55;
      }
    }
  }

  .primary-label {
    width: 150px;
    text-align: center;

    color: map-get($colors, primary);
    font-weight: bold;
    letter-spacing: 1px;
  }
</style>